import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommentDto,
  CommentCreateInput,
  CommentUpdateInput,
  CommentEntityType,
} from '@forest/contracts';
import { api } from '@/lib/api-client';
import type { QueryClient } from '@tanstack/react-query';

/**
 * The list pages render a per-row comment count (e.g. the photos table),
 * so refresh the owning entity's list when its comment set changes.
 */
function invalidateEntityList(qc: QueryClient, entityType: CommentEntityType) {
  const key = entityType === 'PhotoRecord' ? 'photos' : 'parcels';
  void qc.invalidateQueries({ queryKey: [key] });
}

export function useComments(entityType: CommentEntityType, entityId: string | null) {
  return useQuery<CommentDto[]>({
    queryKey: ['comments', entityType, entityId],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set('entityType', entityType);
      sp.set('entityId', entityId!);
      const res = await api.get('comments', { searchParams: sp }).json<{ items: CommentDto[] }>();
      return res.items;
    },
    enabled: !!entityId,
  });
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation<CommentDto, Error, CommentCreateInput>({
    mutationFn: async (input) =>
      api.post('comments', { json: input }).json<CommentDto>(),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comments', variables.entityType, variables.entityId],
      });
      invalidateEntityList(qc, variables.entityType);
    },
  });
}

export function useUpdateComment() {
  const qc = useQueryClient();
  return useMutation<CommentDto, Error, { id: string; input: CommentUpdateInput; entityType: CommentEntityType; entityId: string }>({
    mutationFn: async ({ id, input }) =>
      api.patch(`comments/${id}`, { json: input }).json<CommentDto>(),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comments', variables.entityType, variables.entityId],
      });
      invalidateEntityList(qc, variables.entityType);
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; entityType: CommentEntityType; entityId: string }>({
    mutationFn: async ({ id }) => {
      await api.delete(`comments/${id}`);
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comments', variables.entityType, variables.entityId],
      });
      invalidateEntityList(qc, variables.entityType);
    },
  });
}
